import { createContext, type ReactNode, useContext } from 'react';

/**
 * Whether the calling glass surface is rendered INSIDE the view tree
 * `GlassBlurTarget` nominates — the one Android's frost samples.
 *
 * A surface there must never frost on Android, and not for looks: Dimezis
 * blurs by drawing the target's render node into the blur view's own, so a
 * blur view inside the target makes that graph a cycle and the RenderThread
 * recurses until its stack overflows (SIGSEGV in `libhwui`, the app dies the
 * frame the surface first paints). 3.18.0 shipped exactly that: the coach
 * marks portal into `APP_OVERLAY_PORTAL_HOST`, which sits inside the app-root
 * target, and the Home mark crashed every Android 12+ user on launch.
 *
 * DENY BY DEFAULT, so the next surface cannot repeat it. `GlassBlurTarget`
 * marks its whole subtree; `GlassBackground` refuses to frost anywhere under
 * it (see `canFrostOnAndroid`) and paints its fallback fill instead. React
 * context tracks the NATIVE tree here because every portal the app uses
 * (`@gorhom/portal`, and the bottom-sheet portal built on it) renders its
 * content at the HOST's position: a sheet in the app-root
 * `BottomSheetModalProvider` host — a sibling after the target — reads
 * "outside" and keeps its frost; a coach mark or the full-screen mind map,
 * portalled to the app overlay host inside the target, reads "inside".
 *
 * The one thing context cannot see is a window boundary — see
 * {@link GlassWindowBoundary}.
 */
const InsideGlassBlurTargetContext = createContext(false);

/** `true` when the caller renders inside the nominated blur target. */
export function useIsInsideGlassBlurTarget(): boolean {
  return useContext(InsideGlassBlurTargetContext);
}

/**
 * Marks `children` as the sampled tree. `GlassBlurTarget` owns this; nothing
 * else should render it.
 */
export function GlassBlurTargetScope({ children }: { children: ReactNode }) {
  return (
    <InsideGlassBlurTargetContext.Provider value>
      {children}
    </InsideGlassBlurTargetContext.Provider>
  );
}

/**
 * Wrap the CONTENT of a React Native `Modal` that hosts glass surfaces.
 *
 * Android gives a `Modal` its own window, so nothing in it is part of the
 * target's render node, and Dimezis can safely draw the target into a blur view
 * there — the anchored menus and popovers frost that way today. React context,
 * though, flows through a `Modal` from wherever the element sits, which is
 * usually a screen inside the target; without this boundary those overlays
 * would read "inside" and lose their frost. Losing it is the safe failure — a
 * missing boundary costs a menu its blur, never the app — but it is still a
 * regression, so every `Modal` that hosts a DS overlay renders one.
 *
 * Put it INSIDE the `Modal`, never around it: only the Modal's children are in
 * the other window.
 */
export function GlassWindowBoundary({ children }: { children: ReactNode }) {
  return (
    <InsideGlassBlurTargetContext.Provider value={false}>
      {children}
    </InsideGlassBlurTargetContext.Provider>
  );
}
